import { Component, computed, effect, EventEmitter, forwardRef, Input, Output, Signal, signal } from '@angular/core';
import { GalaxyAbstractFilterInput, GalaxyFilterValueInterface } from '@vendasta/galaxy/filter/chips';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  template: '',
  providers: [
    {
      provide: GalaxyAbstractFilterInput,
      useExisting: forwardRef(() => AbstractAssigneeFilterInputComponent),
    },
  ],
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    GalaxyFormFieldModule,
    MatAutocompleteModule,
  ],
})
export abstract class AbstractAssigneeFilterInputComponent<T> implements GalaxyAbstractFilterInput {
  @Input() set fieldId(value: string) {
    this._fieldId.set(value);
  }

  @Output() valueChanges = new EventEmitter<GalaxyFilterValueInterface[]>();
  formControl: FormControl<T | string> = new FormControl<T | string>('', {
    nonNullable: true,
  });

  searchTerm = toSignal(this.formControl.valueChanges, { initialValue: '' });
  _fieldId = signal('');
  availableOptions = signal<T[]>([]);

  @Input() set initialValue(value: GalaxyFilterValueInterface[]) {
    if (value?.length > 0 && value[0].string) {
      this.formControl.setValue(value[0].string);
    }
  }

  filteredOptions: Signal<T[]> = computed(() => {
    const searchTerm = this.searchTerm();
    const availableOptions = this.availableOptions();
    const filterTerm = this.extractFilterTerm(searchTerm);
    return availableOptions.filter((assignee) => this.filterFn(assignee, filterTerm));
  });
  protected emitSearchTermValue = true;

  protected constructor() {
    effect(() => {
      const value = this.searchTerm();
      if (value === '') {
        return this.emitNewValue([]);
      }
      if (typeof value === 'string') {
        return this.emitNewValue([{ string: value }]);
      }
      return this.emitNewValue([{ string: this.getId(value) }]);
    });
  }

  clearValue() {
    this.formControl.setValue('');
  }

  displayFn(availableOptions: T[]): (assignee: T | string) => string {
    return (assignee: T | string): string => {
      if (!assignee) {
        return '';
      }
      if (typeof assignee === 'string') {
        const foundOption = availableOptions.find((o) => this.getId(o) === assignee);
        return foundOption ? this.getDisplayName(foundOption) : assignee;
      }
      return this.getDisplayName(assignee);
    };
  }

  private emitNewValue(value: GalaxyFilterValueInterface[]): void {
    if (this.emitSearchTermValue) {
      this.valueChanges.emit(value);
    }
  }

  abstract filterFn(assignee: T, searchTerm: string): boolean;
  abstract extractFilterTerm(assignee: T | string): string;
  abstract getId(assignee: T): string;
  abstract getDisplayName(assignee: T): string;
  abstract setAvailableOptions(): void;
}
